import { useEffect, useState } from "react";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtMoney, fmtNumber, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import TenantDetailDrawer from "@/components/admin/TenantDetailDrawer";
import SuperAdminUsers from "@/components/admin/SuperAdminUsers";
import SuperAdminImpersonations from "@/components/admin/SuperAdminImpersonations";
import SuperAdminComped from "@/components/admin/SuperAdminComped";
import SuperAdminLoginMetrics from "@/components/admin/SuperAdminLoginMetrics";
import SuperAdminMarquee from "@/components/admin/SuperAdminMarquee";
import {
  Building2, Users, CreditCard, TrendingUp, Database, FileText,
  Search, ShieldCheck, AlertTriangle, RotateCw, X, Gift,
} from "lucide-react";

const STATUS_OPTIONS = ["active", "trial", "comped", "suspended", "canceled"];
const PLAN_OPTIONS = ["starter", "pro", "business"];

const COMP_CATEGORIES = [
  { value: "partner", label: "Partner" },
  { value: "beta", label: "Beta tester" },
  { value: "support_credit", label: "Support credit" },
  { value: "internal", label: "Internal / staff" },
  { value: "other", label: "Other" },
];

// Available SuperAdmin tabs.
const TABS = [
  { key: "tenants", label: "Tenants" },
  { key: "users", label: "Users" },
  { key: "logins", label: "Login activity" },
  { key: "impersonations", label: "Impersonation audit" },
  { key: "comped", label: "Comped accounts" },
  { key: "settings", label: "Settings" },
];

export default function PlatformAdmin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("tenants");
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [drawerId, setDrawerId] = useState(null);
  const [compPrompt, setCompPrompt] = useState(null); // { tenant, reason, category }

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/tenants", { params: q ? { q } : {} }),
      ]);
      setStats(s.data);
      setTenants(t.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const patchTenant = async (tid, updates) => {
    setBusy(tid);
    try {
      const { data } = await api.patch(`/admin/tenants/${tid}`, updates);
      setTenants((arr) => arr.map((x) => (x.id === tid ? { ...x, ...data } : x)));
      toast.success("Tenant updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(null);
    }
  };

  const openCompPrompt = (t) => setCompPrompt({ tenant: t, reason: "", category: "partner" });
  const submitComp = async () => {
    const { tenant, reason, category } = compPrompt;
    if (!reason || reason.trim().length < 3) {
      toast.error("Please add a reason (min 3 chars)");
      return;
    }
    try {
      await api.post(`/admin/tenants/${tenant.id}/set-free`, {
        reason: reason.trim(), category,
      });
      toast.success(`${tenant.name} is now free`);
      setCompPrompt(null);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Set-free failed");
    }
  };

  if (!user?.is_platform_admin) {
    return (
      <>
        <PageHeader title="Platform admin" subtitle="Access denied" testid="admin-denied-header" />
        <PageBody>
          <div className="ll-card-flat p-10 text-center" data-testid="admin-denied">
            <ShieldCheck size={28} className="mx-auto mb-3 opacity-40" />
            <div className="font-display font-bold text-xl mb-1">Platform admin access required</div>
            <div className="text-sm text-[var(--ll-ink-soft)]">
              Your account isn&apos;t flagged as a platform admin. Ask another admin to promote you.
            </div>
          </div>
        </PageBody>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Platform admin"
        subtitle="Cross-tenant control room"
        testid="admin-header"
        actions={
          <button onClick={loadAll} disabled={loading} className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5 disabled:opacity-60" style={{ borderColor: "var(--ll-border)" }} data-testid="admin-refresh-btn">
            <RotateCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        }
      />
      <PageBody>
        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px mb-6" style={{ background: "var(--ll-border)" }} data-testid="admin-stats">
          <Stat icon={Building2} label="Tenants" value={fmtNumber(stats?.tenants_total || 0)} tint="#EFF6FF" />
          <Stat icon={Users} label="Users" value={fmtNumber(stats?.users_total || 0)} tint="#F0FDF4" />
          <Stat icon={CreditCard} label="Active subs" value={fmtNumber(stats?.active_subscriptions || 0)} tint="#FFF7ED" />
          <Stat icon={TrendingUp} label="MRR" value={fmtMoney(stats?.mrr || 0)} tint="#FEFCE8" accent />
          <Stat icon={CreditCard} label="Trial subs" value={fmtNumber(stats?.trial_subscriptions || 0)} tint="#F1F5F9" />
          <Stat icon={Database} label="Bank accounts" value={fmtNumber(stats?.bank_accounts_total || 0)} tint="#F1F5F9" />
          <Stat icon={FileText} label="Statements" value={fmtNumber(stats?.statements_total || 0)} tint="#F1F5F9" />
          <Stat icon={Database} label="Txns · 30d" value={fmtNumber(stats?.transactions_30d || 0)} sub={`${fmtNumber(stats?.transactions_total || 0)} total`} tint="#F1F5F9" />
        </div>

        {/* Tabs */}
        <div className="border-b flex items-center gap-1 mb-6" style={{ borderColor: "var(--ll-border)" }} data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px ${
                tab === t.key
                  ? "border-[var(--ll-ink)] font-bold"
                  : "border-transparent text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "tenants" && (
          <div className="ll-card-flat overflow-hidden">
            <div className="px-5 py-3 border-b flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "var(--ll-border)" }}>
              <div className="font-display font-bold text-lg">Tenants</div>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") loadAll(); }}
                  placeholder="Search by name or owner email…"
                  data-testid="admin-tenant-search"
                  className="pl-9 pr-3 py-1.5 border text-sm w-[280px]"
                  style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
            </div>
            {tenants.length === 0 ? (
              <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="admin-tenants-empty">
                No tenants match.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="admin-tenants-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] bg-[var(--ll-bg-sidebar)] border-b" style={{ borderColor: "var(--ll-border)" }}>
                    <th className="px-4 py-3 font-medium">Tenant</th>
                    <th className="px-4 py-3 font-medium">Owner</th>
                    <th className="px-4 py-3 font-medium">Plan</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium text-right">Users</th>
                    <th className="px-4 py-3 font-medium text-right">Accts</th>
                    <th className="px-4 py-3 font-medium text-right">Txns</th>
                    <th className="px-4 py-3 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((t) => (
                    <tr key={t.id} className="border-b hover:bg-[var(--ll-row-hover)]" style={{ borderColor: "var(--ll-border)" }} data-testid={`admin-tenant-row-${t.id}`}>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setDrawerId(t.id)}
                          data-testid={`admin-tenant-open-${t.id}`}
                          className="text-left hover:underline"
                        >
                          <div className="font-medium truncate max-w-[200px]">{t.name}</div>
                          <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{t.id.slice(0, 8)}</div>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-[var(--ll-ink-soft)] truncate max-w-[200px]">{t.owner_email || "—"}</td>
                      <td className="px-4 py-3">
                        <select
                          value={t.plan || "trial"}
                          onChange={(e) => patchTenant(t.id, { plan: e.target.value })}
                          disabled={busy === t.id}
                          data-testid={`admin-plan-${t.id}`}
                          className="px-2 py-1 border text-xs bg-white"
                          style={{ borderColor: "var(--ll-border)" }}
                        >
                          {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={t.subscription_status || "trial"}
                          onChange={(e) => patchTenant(t.id, { subscription_status: e.target.value })}
                          disabled={busy === t.id}
                          data-testid={`admin-status-${t.id}`}
                          className="px-2 py-1 border text-xs bg-white"
                          style={{ borderColor: "var(--ll-border)" }}
                        >
                          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        {t.subscription_status === "suspended" && (
                          <span className="ml-1.5 inline-flex items-center text-[var(--ll-ochre-ink)]" title="Suspended">
                            <AlertTriangle size={11} />
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono-fin text-right">{fmtNumber(t.users || 0)}</td>
                      <td className="px-4 py-3 font-mono-fin text-right">{fmtNumber(t.bank_accounts || 0)}</td>
                      <td className="px-4 py-3 font-mono-fin text-right">{fmtNumber(t.transactions || 0)}</td>
                      <td className="px-4 py-3 text-[var(--ll-ink-soft)] text-xs whitespace-nowrap">
                        <div className="flex items-center gap-1 justify-end">
                          <span>{fmtDate(t.created_at)}</span>
                          {t.subscription_status !== "comped" && (
                            <button
                              onClick={() => openCompPrompt(t)}
                              data-testid={`admin-set-free-${t.id}`}
                              title="Set as free / comped"
                              className="text-[10px] px-1.5 py-0.5 border hover:bg-[#FEF3C7] flex items-center gap-1"
                              style={{ borderColor: "var(--ll-border)" }}
                            >
                              <Gift size={10} /> Set free
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "users" && <SuperAdminUsers />}
        {tab === "logins" && <SuperAdminLoginMetrics />}
        {tab === "impersonations" && <SuperAdminImpersonations />}
        {tab === "comped" && <SuperAdminComped />}
        {tab === "settings" && <SuperAdminMarquee />}
      </PageBody>
      <TenantDetailDrawer tenantId={drawerId} onClose={() => { setDrawerId(null); loadAll(); }} />

      {/* Comp reason prompt */}
      {compPrompt && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setCompPrompt(null)} data-testid="admin-comp-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white border w-full max-w-md p-6" style={{ borderColor: "var(--ll-ink)" }}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="font-display font-bold text-xl flex items-center gap-2"><Gift size={16} /> Comp this tenant</h2>
                <p className="text-xs text-[var(--ll-ink-soft)] mt-1">
                  Marks <b>{compPrompt.tenant.name}</b> as a free account. The reason will show up in the quarterly review.
                </p>
              </div>
              <button onClick={() => setCompPrompt(null)} aria-label="Close" className="text-[var(--ll-ink-soft)]"><X size={16} /></button>
            </div>
            <label className="block mb-3">
              <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Category</span>
              <select
                value={compPrompt.category}
                onChange={(e) => setCompPrompt({ ...compPrompt, category: e.target.value })}
                data-testid="admin-comp-category"
                className="mt-1 w-full px-3 py-2 border bg-white text-sm"
                style={{ borderColor: "var(--ll-border)" }}
              >
                {COMP_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Reason (required)</span>
              <textarea
                value={compPrompt.reason}
                onChange={(e) => setCompPrompt({ ...compPrompt, reason: e.target.value })}
                placeholder="e.g. Design partner — 12-month free access approved by Priya"
                data-testid="admin-comp-reason"
                rows={3}
                className="mt-1 w-full px-3 py-2 border text-sm"
                style={{ borderColor: "var(--ll-border)" }}
                autoFocus
              />
            </label>
            <div className="pt-4 flex items-center justify-end gap-2">
              <button onClick={() => setCompPrompt(null)} className="px-4 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}>Cancel</button>
              <button
                onClick={submitComp}
                data-testid="admin-comp-confirm"
                className="px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] text-sm"
              >
                Grant free access
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Stat({ icon: Icon, label, value, sub, tint, accent }) {
  return (
    <div className="p-5" style={{ background: tint || "var(--ll-bg-card)", borderTop: accent ? "3px solid var(--ll-ink)" : "none" }} data-testid={`admin-stat-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">{label}</div>
        <Icon size={14} className="opacity-70" />
      </div>
      <div className="font-mono-fin text-2xl leading-none">{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mt-2">{sub}</div>}
    </div>
  );
}
